import { enqueueSnackbar } from 'notistack'
import { AxiosResponse } from 'axios'
import { axiosInstanceEntity } from './axiosInstance'
import {
    IWorkflowGetApiResponse,
    IAttribute,
    IRelation,
    RelationDropdowns,
    IRule,
    EntityType,
    MataDataType,
} from '../components/DTStudio/interfaces'
import {
    GetAttributeResponse,
    GetBusinessRulesResponse,
    GetEntityTypesAPIResponse,
    GetRelationsResponse,
    GetSharedOrganizations,
    IAddAttributeParams,
    IAddRelationParams,
    IAddWorkFlowParams,
    IAddWorkFlowPayload,
    ICloneOrAddEntityPayload,
    ICreateRuleSetPayload,
    IFetchBusinessRulesParams,
    IUpdateAttributeParams,
    IUpdateEntityPayload,
    IUpdateRelationParams,
    GetLogos,
    IOptions,
} from './apiInterfaces'
import { ILogo } from '../components/DTStudio/Settings'
import { PostTemplateSharedOrgsApi } from '../components/DTStudio/Settings/ShareTemplate'
import i18n from '../i18n'

// Utility functions

export const handleResponse = <T>(
    response: AxiosResponse<T>,
    successMessage?: string,
): T => {
    if (response.status >= 200 && response.status < 300) {
        if (response && successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return response.data
    } else {
        throw new Error('Error')
    }
}

export const handleError = (
    err: unknown,
    errorMessage?: string,
    throwError = false,
): string | void => {
    let message: string
    if (typeof err === 'string') {
        message = err
    } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response: { data: { message: string } } }
        message =
            axiosError.response.data.message ||
            errorMessage ||
            i18n.t('anErrorOccurred')
    } else if (err instanceof Error) {
        message = err.message
    } else {
        message = errorMessage || i18n.t('anErrorOccurred')
    }
    if (throwError) {
        return message
    }
    enqueueSnackbar(message, { variant: 'error' })
}

export const makeRequest = async <T, D = null, P = null>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: D,
    params?: P,
    successMessage?: string,
    errorMessage?: string,
): Promise<T> => {
    try {
        const response: AxiosResponse<T> = await axiosInstanceEntity({
            method,
            url,
            data,
            params,
        })
        return handleResponse(response, successMessage)
    } catch (err) {
        handleError(err, errorMessage)
        return undefined as unknown as T
    }
}

// Entity Services
export const getEntityTemplates = async (
    orgId: string,
): Promise<EntityType[]> => {
    const response = await makeRequest<GetEntityTypesAPIResponse>(
        'get',
        `/template/${orgId}`,
    )
    return response.data
}

// UBQ-5606: the org's shipment templates, resolved server-side from the "shipment template"
// settings row. Used to keep Shipment in the bulk upload picker while it stays Hidden in nav.
export const getShipmentTemplateIds = async (
    orgId: string,
): Promise<string[]> => {
    const response = await makeRequest<
        { shipmentTemplateIds: string[] },
        null,
        { orgId: string }
    >('get', '/coldChain/shipmentTemplates', null, { orgId })
    return response?.shipmentTemplateIds ?? []
}

export const cloneOrAddEntity = async (
    orgId: string,
    data: ICloneOrAddEntityPayload,
    isCloning = false,
): Promise<void> => {
    const path = isCloning
        ? `/template/clone/${orgId}`
        : `/template?orgId=${orgId}`
    const message1 = isCloning ? 'cloned' : 'added'
    await makeRequest<void, ICloneOrAddEntityPayload>(
        'post',
        path,
        data,
        null,
        `Entity is ${message1} successfully!`,
        `Error while ${message1} entity!`,
    )
}

export const updateEntity = async (
    orgId: string,
    data: IUpdateEntityPayload,
): Promise<void> => {
    await makeRequest<void, IUpdateEntityPayload>(
        'put',
        `/template/${orgId}`,
        data,
        null,
        i18n.t('entityUpdated'),
        i18n.t('errorUpdatingEntity'),
    )
}

export const deleteEntity = async (
    orgId: string,
    entityTemplateId: string,
): Promise<void> => {
    await makeRequest<void>(
        'delete',
        `/template/${orgId}/${entityTemplateId}`,
        null,
        null,
        i18n.t('entityDeleted'),
        i18n.t('errorDeletingEntity'),
    )
}

// Attributes Services
export const fetchAttributes = async (
    orgId: string,
    entityTemplateId: string,
    options?: IOptions,
): Promise<IAttribute[]> => {
    const optionsString = Object.entries(options || {})
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
    const queryString = optionsString ? `&${optionsString}` : ''

    const response = await makeRequest<GetAttributeResponse>(
        'get',
        `/attributes?orgId=${orgId}&entityTemplateId=${entityTemplateId}${queryString}`,
    )
    return response.attributeResponse
}

export const addAttribute = async (
    orgId: string,
    entityTemplateId: string,
    attribute: IAttribute,
): Promise<void> => {
    await makeRequest<void, IAttribute, IAddAttributeParams>(
        'post',
        `/attributes`,
        attribute,
        {
            entityTemplateId,
            orgId,
        },
        i18n.t('attributeAdded'),
        i18n.t('errorAddingAttribute'),
    )
}

export const updateAttribute = async (
    orgId: string,
    entityTemplateId: string,
    attributePrevId: string, //based this id attribute data is updating
    attribute: IAttribute,
): Promise<void> => {
    await makeRequest<void, IAttribute, IUpdateAttributeParams>(
        'put',
        `/attributes?orgId=${orgId}`,
        attribute,
        {
            entityTemplateId,
            attributeId: attributePrevId,
        },
        i18n.t('attributeUpdated'),
        i18n.t('errorUpdatingAttribute'),
    )
}

export const deleteAttribute = async (
    orgId: string,
    entityTemplateId: string,
    attributeId: string,
): Promise<void> => {
    await makeRequest<void>(
        'delete',
        `/attributes?orgId=${orgId}&attributeId=${attributeId}&entityTemplateId=${entityTemplateId}`,
        null,
        null,
        i18n.t('attributeDeleted'),
        i18n.t('errorDeletingAttribute'),
    )
}

export const fetchDataTypes = async (orgId: string): Promise<string[]> => {
    const response = await makeRequest<string[]>(
        'get',
        `/attributes/getTypes?orgId=${orgId}`,
    )
    return response
}

export const fetchMetaPropertiesOfAttributes = async (
    orgId: string,
): Promise<MataDataType> => {
    const response = await makeRequest<MataDataType>(
        'get',
        `/attributes/metadata?orgId=${orgId}`,
    )
    return response
}

// Relations Services
export const fetchAllDropDownsValuesForRelationsPage = (
    orgId: string,
    entityTemplateId: string,
): Promise<RelationDropdowns> => {
    return makeRequest<RelationDropdowns>(
        'get',
        `/relation/relationFields/${orgId}/${entityTemplateId}`,
    )
}

export const fetchRelations = async (
    entityId: string,
    orgId: string,
): Promise<IRelation[]> => {
    const response = await makeRequest<GetRelationsResponse>(
        'get',
        `/relation/${entityId}?orgId=${orgId}`,
    )
    return response?.entityRelationData ?? []
}

export const addRelation = async (
    entityTemplateId: string,
    orgId: string,
    relation: IRelation,
): Promise<void> => {
    await makeRequest<void, IRelation, IAddRelationParams>(
        'post',
        `/relation`,
        relation,
        {
            entityTemplateId,
            orgId,
        },
        i18n.t('relationAdded'),
        i18n.t('errorAddingRelation'),
    )
}

export const updateRelation = async (
    entityTemplateId: string,
    orgId: string,
    relation: IRelation,
): Promise<void> => {
    await makeRequest<void, IRelation, IUpdateRelationParams>(
        'put',
        `/relation`,
        relation,
        {
            entityTemplateId,
            orgId,
            relId: relation.relKey,
        },
        i18n.t('relationUpdated'),
        i18n.t('errorUpdatingRelation'),
    )
}

export const deleteRelation = async (
    entityTemplateId: string,
    relKey: string,
    orgId: string,
): Promise<void> => {
    await makeRequest<void, null, { orgId: string }>(
        'delete',
        `/relation/${relKey}/${entityTemplateId}`,
        null,
        { orgId },
        i18n.t('relationDeleted'),
        i18n.t('errorDeletingRelation'),
    )
}

// Business Rules Services
export const fetchBusinessRules = async (
    orgId: string,
    entityTemplateId: string,
): Promise<GetBusinessRulesResponse> => {
    const response = await makeRequest<
        GetBusinessRulesResponse,
        null,
        IFetchBusinessRulesParams
    >('get', `/rule/v2/${orgId}`, null, {
        entityTemplateId,
    })
    return response
}

export const createRuleSet = async (
    orgId: string,
    entityTemplateId: string,
    ruleSetName: string,
    isDefault: boolean,
): Promise<void> => {
    await makeRequest<void, ICreateRuleSetPayload>(
        'post',
        `/rule/ruleset/${entityTemplateId}/${orgId}`,
        { name: ruleSetName, isDefault },
        null,
        i18n.t('ruleSetCreated'),
        i18n.t('errorCreatingRuleSet'),
    )
}

export const updateRuleSet = async (
    orgId: string,
    ruleSetId: string,
    ruleSetName: string,
    isDefault: boolean,
): Promise<void> => {
    await makeRequest<void, ICreateRuleSetPayload>(
        'put',
        `/rule/ruleset/${ruleSetId}/${orgId}`,
        { name: ruleSetName, isDefault },
        null,
        i18n.t('ruleSetUpdated'),
        i18n.t('errorUpdatingRuleSet'),
    )
}

export const deleteRuleSet = async (
    orgId: string,
    ruleSetId: string,
): Promise<void> => {
    await makeRequest<void>(
        'delete',
        `/rule/ruleSet?ruleSetId=${ruleSetId}&orgId=${orgId}`,
        null,
        null,
        i18n.t('ruleDeleted'),
        i18n.t('errorDeletingRule'),
    )
}

export const addRule = async (
    orgId: string,
    ruleSetId: string,
    rule: IRule,
): Promise<any> => {
    return await makeRequest<void, IRule>(
        'post',
        `/rule/v2/${ruleSetId}/${orgId}`,
        rule,
        null,
        i18n.t('ruleCreated'),
        i18n.t('errorCreatingRule'),
    )
}

export const updateRule = async (
    orgId: string,
    rule: { attributeValues: Record<string, string> },
): Promise<any> => {
    const ruleId = rule.attributeValues['rule@id'] || ''
    return await makeRequest<void, { attributeValues: Record<string, string> }>(
        'put',
        `/rule/v2/${ruleId}/${orgId}`,
        rule,
        null,
        i18n.t('ruleUpdated'),
        i18n.t('errorUpdatingRule'),
    )
}

export const deleteRule = async (
    orgId: string,
    ruleId: string,
): Promise<void> => {
    await makeRequest<void>(
        'delete',
        `/rule?ruleId=${ruleId}&orgId=${orgId}`,
        null,
        null,
        i18n.t('ruleDeleted'),
        i18n.t('errorDeletingRule'),
    )
}

export const fetchBusinessRulesAttributes = async (
    orgId: string,
    templateId: string,
): Promise<any[]> => {
    return await axiosInstanceEntity
        .get(`/entity?orgId=${orgId}&templateId=${templateId}&entityId=${''}`)
        .then((response) => {
            return response?.data || []
        })
        .catch((err) => {
            return err.response
        })
}

export const fetchBusinessRulesDataByID = async (
    ruleId: string,
    orgId: string,
): Promise<any[]> => {
    const response = await makeRequest<any, null, any>(
        'get',
        `/rule/v2?ruleId=${ruleId}&orgId=${orgId}`,
        null,
    )
    return response
}

export const fetchRulesSubTemplates = async (orgId: string): Promise<any[]> => {
    const response = await makeRequest<any, null, any>(
        'get',
        `/rule/v2/subTypes?orgId=${orgId}`,
        null,
    )
    return response
}

// Workflow Services
export const fetchWorkflows = async (
    entityTemplateId: string,
    orgId: string,
): Promise<IWorkflowGetApiResponse> => {
    return await makeRequest<IWorkflowGetApiResponse>(
        'get',
        `/workflow/${entityTemplateId}/${orgId}`,
    )
}

export const addWorkFlow = async (
    entityTemplateId: string,
    orgId: string,
    workflow: IAddWorkFlowPayload,
): Promise<void> => {
    await makeRequest<void, IAddWorkFlowPayload, IAddWorkFlowParams>(
        'post',
        `/workflow`,
        workflow,
        {
            entityTemplateId,
            orgId,
        },
        i18n.t('workflowUpdated'),
        i18n.t('errorUpdatingWorkflow'),
    )
}

export const fetchSharedOrgsByTemId = async (
    entityTemplateId: string,
    orgId: string,
    params: unknown,
): Promise<GetSharedOrganizations> => {
    const response = await makeRequest<GetSharedOrganizations, null, unknown>(
        'get',
        `/template/share/organizations?entityTemplateId=${entityTemplateId}&orgId=${orgId}`,
        null,
        params,
    )
    return response
}

export const updatedSharedOrgs = async (
    entityTemplateId: string,
    orgId: string,
    orgsList: PostTemplateSharedOrgsApi,
): Promise<void> => {
    await makeRequest<void, PostTemplateSharedOrgsApi>(
        'post',
        `/template/share?entityTemplateId=${entityTemplateId}&orgId=${orgId}`,
        orgsList,
        null,
        i18n.t('entitySharingUpdated'),
        i18n.t('errorSharingEntity'),
    )
}

export const fetchAllLogos = async (
    entityTemplateId: string,
    orgId: string,
): Promise<GetLogos> => {
    const response = await makeRequest<GetLogos>(
        'get',
        `/template/logo?orgId=${orgId}`,
    )
    return response
}

export const fetchSelectedLogo = async (
    entityTemplateId: string,
    orgId: string,
): Promise<ILogo> => {
    const response = await makeRequest<ILogo>(
        'get',
        `/template/logo/${entityTemplateId}?&orgId=${orgId}`,
    )
    return response
}

export const updateTemplate = async (
    entityTemplateId: string,
    orgId: string,
    templateData: unknown,
): Promise<void> => {
    await makeRequest<void, any>(
        'put',
        `/template/${orgId}`,
        templateData,
        null,
        '', // 'logo is updated successfully!',
        i18n.t('errorUpdatingLogo'),
    )
}

export const fetchBusinessRulesetsByRulesetsIDs = async (
    orgId: string,
    entityTemplateId: string,
    rulesetIds: string,
): Promise<ILogo> => {
    const response = await makeRequest<ILogo>(
        'get',
        // `/rule/v2/${orgId}/ruleSet?entityTemplateId=${entityTemplateId}&ruleSetIds=${rulesetIds}`,
        `/rule/v2/rulesByRuleSet?ruleSetIds=${rulesetIds}`,
    )
    return response
}
